import { supabase } from '../lib/supabase';
import {
  getGoogleClientId,
  getGoogleRedirectUri,
  getMicrosoftClientId,
  getMicrosoftRedirectUri,
  getZoomClientId,
  getZoomRedirectUri,
} from './oauthConfigService';
import { GOOGLE_UNIFIED_SCOPES } from './googleScopes';

export type VideoProvider = 'zoom' | 'google_meet' | 'microsoft_teams';

export interface VideoConnection {
  id: string;
  client_id: string;
  user_id: string;
  provider: VideoProvider;
  provider_user_id: string;
  provider_account_id: string;
  email: string;
  display_name: string;
  api_base_url: string;
  expires_at: string;
  is_active: boolean;
  connected_at: string;
  created_at: string;
  updated_at: string;
  sales_member_id?: string | null;
}

export type ZoomConnection = VideoConnection;

export interface ZoomMeeting {
  meeting_id: number;
  join_url: string;
  start_url: string;
  topic: string;
  start_time: string;
  duration: number;
  password: string | null;
  timezone: string;
}

export interface GoogleMeetMeeting {
  event_id: string;
  join_url: string;
  html_link: string;
  topic: string;
  start_time: string;
  duration: number;
  timezone: string;
}

export interface CreateMeetingParams {
  client_id: string;
  topic: string;
  start_time: string;
  duration?: number;
  timezone?: string;
  agenda?: string;
  settings?: {
    waiting_room?: boolean;
    join_before_host?: boolean;
    auto_recording?: 'none' | 'local' | 'cloud';
  };
}

export interface CreateGoogleMeetParams {
  client_id: string;
  topic: string;
  start_time: string;
  duration?: number;
  timezone?: string;
  agenda?: string;
  attendees?: string[];
}

export interface MicrosoftTeamsMeeting {
  meeting_id: string;
  join_url: string;
  topic: string;
  start_time: string;
  duration: number;
  timezone: string;
}

export interface CreateMicrosoftTeamsParams {
  client_id: string;
  topic: string;
  start_time: string;
  duration?: number;
  timezone?: string;
  agenda?: string;
  attendees?: string[];
}

const ZOOM_OAUTH_URL = 'https://zoom.us/oauth/authorize';
const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const MICROSOFT_OAUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const MICROSOFT_TEAMS_SCOPES = 'OnlineMeetings.ReadWrite User.Read Calendars.ReadWrite offline_access';

export const zoomService = {
  initiateZoomOAuth: async (clientId: string, userId: string, salesMemberId?: string): Promise<string> => {
    const zoomClientId = await getZoomClientId();
    const redirectUri = await getZoomRedirectUri();

    if (!zoomClientId || !redirectUri) {
      throw new Error('Zoom OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId, sales_member_id: salesMemberId || undefined });

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: zoomClientId,
      redirect_uri: redirectUri,
      state,
    });

    return `${ZOOM_OAUTH_URL}?${params.toString()}`;
  },

  handleOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string,
    salesMemberId?: string
  ): Promise<{ success: boolean; connection?: ZoomConnection; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getZoomRedirectUri();
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/zoom-oauth-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          code,
          client_id: clientId,
          user_id: userId,
          redirect_uri: redirectUri,
          sales_member_id: salesMemberId || undefined,
        }),
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Zoom' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getZoomConnection: async (clientId: string, salesMemberId?: string): Promise<ZoomConnection | null> => {
    try {
      let query = supabase
        .from('video_connections')
        .select('*')
        .eq('client_id', clientId)
        .eq('provider', 'zoom')
        .eq('is_active', true);

      if (salesMemberId) {
        query = query.eq('sales_member_id', salesMemberId);
      } else {
        query = query.is('sales_member_id', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('Error fetching Zoom connection:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting Zoom connection:', error);
      return null;
    }
  },

  disconnectZoom: async (clientId: string, salesMemberId?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/zoom-disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ client_id: clientId, sales_member_id: salesMemberId || undefined }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to disconnect Zoom' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  createMeeting: async (
    params: CreateMeetingParams
  ): Promise<{ success: boolean; meeting?: ZoomMeeting; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-zoom-meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to create meeting' };
      }

      return { success: true, meeting: data.meeting };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getActiveVideoConnection: async (clientId: string, userId?: string, salesMemberId?: string): Promise<VideoConnection | null> => {
    try {
      let query = supabase
        .from('video_connections')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true);

      if (salesMemberId) {
        query = query.eq('sales_member_id', salesMemberId);
      } else if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        console.error('Error fetching video connection:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting video connection:', error);
      return null;
    }
  },

  initiateGoogleMeetOAuth: async (clientId: string, userId: string, salesMemberId?: string): Promise<string> => {
    const googleClientId = await getGoogleClientId();
    const redirectUri = await getGoogleRedirectUri('meet');

    if (!googleClientId || !redirectUri) {
      throw new Error('Google Meet OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId, sales_member_id: salesMemberId || undefined });

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: googleClientId,
      redirect_uri: redirectUri,
      scope: GOOGLE_UNIFIED_SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state,
    });

    return `${GOOGLE_OAUTH_URL}?${params.toString()}`;
  },

  handleGoogleMeetOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string,
    salesMemberId?: string
  ): Promise<{ success: boolean; connection?: VideoConnection; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getGoogleRedirectUri('meet');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/google-meet-oauth-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          code,
          client_id: clientId,
          user_id: userId,
          redirect_uri: redirectUri,
          sales_member_id: salesMemberId || undefined,
        }),
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Google Meet' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  disconnectGoogleMeet: async (clientId: string, salesMemberId?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/google-meet-disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ client_id: clientId, sales_member_id: salesMemberId || undefined }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to disconnect Google Meet' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  createGoogleMeetMeeting: async (
    params: CreateGoogleMeetParams
  ): Promise<{ success: boolean; meeting?: GoogleMeetMeeting; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-google-meet-meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to create Google Meet meeting' };
      }

      return { success: true, meeting: data.meeting };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  initiateMicrosoftTeamsOAuth: async (clientId: string, userId: string, salesMemberId?: string): Promise<string> => {
    const microsoftClientId = await getMicrosoftClientId();
    const redirectUri = await getMicrosoftRedirectUri('teams');

    if (!microsoftClientId || !redirectUri) {
      throw new Error('Microsoft Teams OAuth not configured. Check oauth_config table.');
    }

    const state = JSON.stringify({ client_id: clientId, user_id: userId, sales_member_id: salesMemberId || undefined });

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: microsoftClientId,
      redirect_uri: redirectUri,
      scope: MICROSOFT_TEAMS_SCOPES,
      response_mode: 'query',
      prompt: 'consent',
      state,
    });

    return `${MICROSOFT_OAUTH_URL}?${params.toString()}`;
  },

  handleMicrosoftTeamsOAuthCallback: async (
    code: string,
    clientId: string,
    userId: string,
    salesMemberId?: string
  ): Promise<{ success: boolean; connection?: VideoConnection; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = await getMicrosoftRedirectUri('teams');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/microsoft-teams-oauth-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          code,
          client_id: clientId,
          user_id: userId,
          redirect_uri: redirectUri,
          sales_member_id: salesMemberId || undefined,
        }),
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        return { success: false, error: 'Invalid response from server' };
      }

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to connect Microsoft Teams' };
      }

      return { success: true, connection: data.connection };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  disconnectMicrosoftTeams: async (clientId: string, salesMemberId?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/microsoft-teams-disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ client_id: clientId, sales_member_id: salesMemberId || undefined }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to disconnect Microsoft Teams' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  createMicrosoftTeamsMeeting: async (
    params: CreateMicrosoftTeamsParams
  ): Promise<{ success: boolean; meeting?: MicrosoftTeamsMeeting; error?: string }> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-microsoft-teams-meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to create Microsoft Teams meeting' };
      }

      return { success: true, meeting: data.meeting };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  getVideoConnectionForMember: async (clientId: string, salesMemberId: string): Promise<VideoConnection | null> => {
    try {
      const { data, error } = await supabase
        .from('video_connections')
        .select('*')
        .eq('client_id', clientId)
        .eq('sales_member_id', salesMemberId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching video connection for member:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting video connection for member:', error);
      return null;
    }
  },

  getVideoConnectionsForMembers: async (clientId: string, memberIds: string[]): Promise<VideoConnection[]> => {
    try {
      const { data, error } = await supabase
        .from('video_connections')
        .select('*')
        .eq('client_id', clientId)
        .in('sales_member_id', memberIds)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching video connections for members:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting video connections for members:', error);
      return [];
    }
  },
};
